// Mock implementation of ISubmissionFlowApi.
// Simulates latency and rejects with the SAME shapes src/shared/http.ts
// produces: HTTP errors reject with the parsed body (ISubmissionApiError
// here), connectivity failures reject with an INetworkError-shaped object —
// so calling code cannot tell mock from real.

import {
  getForcedCountryCode,
  getMockLatencyMs,
  getMockScenario,
  MockScenario,
} from "./config";
import {
  AGENCY_AGENTS,
  AGENCY_OPTIONS,
  buildCastingCalls,
  listedCountryFor,
  withLateRule,
  CITY_OPTIONS,
  buildClosedBreakdown,
  buildClosingTodayBreakdown,
  buildCrossBorderBreakdown,
  buildEmptyCastingCalls,
  buildOpenBreakdown,
  buildPaywallOffer,
  buildRoleCastBreakdown,
  buildAllCastBreakdown,
  buildStaleDeadlineBreakdown,
  buildListedBreakdown,
  GEO_CA,
  GEO_MX,
  MOCK_IDS,
  resolveAccountCity,
  TRIAL_DAYS,
} from "./fixtures";
import {
  IAgencyOptionDto,
  IAgentOptionDto,
  IUnsubscribeResponse,
  IBreakdownCityDto,
  IBreakdownSubmissionInfoDto,
  ICastingCallListDto,
  ICastingCallsQuery,
  ICheckoutStatusDto,
  IDirectCheckoutResponse,
  IHoldSubmissionDto,
  IHoldSubmissionRequest,
  IHomeLocationDto,
  IMediaAssetDto,
  IPaywallExposureRequest,
  IExperimentExposureResponse,
  IPaywallOfferDto,
  IRepresentationDto,
  IResendCodeResponse,
  ISaveMaterialsRequest,
  ISaveForLaterRequest,
  ISaveForLaterResponse,
  FinishLaterEmailStatus,
  IPhoneStatusDto,
  ISendPhoneCodeRequest,
  ISendSubmissionRequest,
  ISendSubmissionResponse,
  ISetHomeLocationRequest,
  IAccountNameDto,
  IClassicSessionDto,
  ITabSignInDto,
  ISetRepresentationRequest,
  ISendPhoneCodeResponse,
  ISignupStateResponse,
  IStartCheckoutRequest,
  IStartCheckoutResponse,
  IStartSignupRequest,
  IStartSignupResponse,
  ISubmissionApiError,
  ISubmissionDraftDto,
  ISubmissionHomeDto,
  ISubmissionFlowApi,
  ISubmissionGeoGuessDto,
  ISubmissionMembershipDto,
  ISubmissionReviewDto,
  IUploadMediaRequest,
  IVerifyEmailResponse,
  IVerifyPhoneOptions,
  IVerifyPhoneResponse,
  MediaKind,
  MockCheckoutOutcome,
  Step4Variant,
} from "./types";
import { isValidPassword } from "../../../components/Submission/common/validation";
import { isValidEmail } from "../../../helpers/validations";
import {
  clearMockAssets,
  getMockAsset,
  listMockAssets,
  putMockAsset,
} from "./mockAssets";
import {
  clearMockSession,
  createMockSession,
  EMAIL_CODE_TTL_MIN,
  EMAIL_RESEND_COOLDOWN_S,
  IMockSession,
  isoIn,
  maskEmail,
  maskPhone,
  MAX_VERIFY_ATTEMPTS,
  MOCK_EMAIL_CODE,
  MOCK_PHONE_CODE,
  PHONE_CODE_TTL_MIN,
  PHONE_RESEND_COOLDOWN_S,
  readMockSession,
  writeMockSession,
} from "./mockSession";

const SLOW_MS = 8000;

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const latencyMs = (): number => {
  const forced = getMockLatencyMs();
  if (forced !== null) return forced;
  return 250 + Math.random() * 450;
};

const apiError = (
  status: number,
  errorCode: ISubmissionApiError["errorCode"],
  message: string,
  retryAfterSeconds?: number
): ISubmissionApiError => ({ status, errorCode, message, retryAfterSeconds });

// Same shape INetworkError gets in src/shared/http.ts (isNetworkError flag),
// so isNetworkError() type guards work against mock failures too.
const networkError = (): Error => {
  const error: any = new Error(
    "We couldn't reach the server. Please check your connection and try again in a moment."
  );
  error.name = "NetworkError";
  error.isNetworkError = true;
  error.originalError = new TypeError("Failed to fetch (mock offline scenario)");
  return error;
};

// Cross-cutting scenarios every endpoint honors before returning its payload.
const applySharedScenario = async (scenario: MockScenario): Promise<void> => {
  switch (scenario) {
    case "slow":
      await delay(SLOW_MS);
      return;
    case "offline":
      await delay(latencyMs());
      throw networkError();
    case "error500":
      await delay(latencyMs());
      throw apiError(500, "serverError", "Mock: internal server error");
    case "rateLimit429":
      await delay(latencyMs());
      throw apiError(429, "rateLimited", "Mock: too many requests", 60);
    default:
      await delay(latencyMs());
  }
};

const logCall = (name: string, detail: any) => {
  // eslint-disable-next-line no-console
  console.info(`[mock-api] ${name}`, detail, `(scenario: ${getMockScenario()})`);
};

const mockSubmissionFlowApi: ISubmissionFlowApi = {
  async getBreakdownSubmissionInfo(
    breakdownId: string,
    roleId?: string
  ): Promise<IBreakdownSubmissionInfoDto> {
    const scenario = getMockScenario();
    logCall("getBreakdownSubmissionInfo", { breakdownId, roleId });
    await applySharedScenario(scenario);

    // Scenario overrides first, then well-known ids so bookmarked demo URLs
    // behave the same in every scenario.
    if (scenario === "notFound" || breakdownId === MOCK_IDS.notFoundBreakdown) {
      throw apiError(404, "breakdownNotFound", "Mock: breakdown not found");
    }
    // Fields the real API always sends: the classic ref (the mock's refs are
    // already classic), the director and paid flags, and the closed reason.
    const withEntryFlags = (info: IBreakdownSubmissionInfoDto): IBreakdownSubmissionInfoDto => ({
      ...info,
      classicRef: info.classicRef || info.breakdownId,
      isVerifiedDirector: info.isVerifiedDirector !== undefined ? info.isVerifiedDirector : true,
      isPaid: info.isPaid !== undefined ? info.isPaid : true,
      closedReason: info.status === "Closed" ? info.closedReason || "expired" : undefined,
    });
    if (scenario === "expired" || breakdownId === MOCK_IDS.closedBreakdown) {
      return withEntryFlags({ ...buildClosedBreakdown(roleId), breakdownId });
    }
    // ?mockScenario=roleCast: whatever the link names, its role has been cast. The
    // cast-role breakdown itself answers the role asked for, as the API does.
    if (scenario === "roleCast") {
      return withEntryFlags({ ...buildRoleCastBreakdown(), breakdownId });
    }
    if (breakdownId === MOCK_IDS.roleCastBreakdown) {
      return withEntryFlags({ ...buildRoleCastBreakdown(Date.now(), roleId), breakdownId });
    }
    // Well-known ids, shared with the step-4 screens so the same breakdown is
    // never described two different ways. Unknown ids echo back as an open
    // breakdown, so deep links keep their identity.
    return withEntryFlags(breakdownFixture(breakdownId, roleId));
  },

  async getCastingCalls(query?: ICastingCallsQuery): Promise<ICastingCallListDto> {
    const scenario = getMockScenario();
    logCall("getCastingCalls", query || {});
    await applySharedScenario(scenario);

    // The real API reads the visitor's country from the IP; the mock's stand-in
    // for "the IP" is ?ctry=, then Canada. A country the visitor may not switch
    // to (Mexico, for a Canadian) is answered with their own, as the API does.
    const visitor = (getForcedCountryCode() || "CA").toUpperCase();
    const countryCode = listedCountryFor(visitor, (query && query.countryCode) || visitor);
    if (scenario === "empty") {
      return buildEmptyCastingCalls(Date.now(), undefined, countryCode);
    }
    return buildCastingCalls(Date.now(), undefined, countryCode, undefined, visitor);
  },

  async getRequesterLocation(): Promise<ISubmissionGeoGuessDto> {
    const scenario = getMockScenario();
    logCall("getRequesterLocation", {});
    await applySharedScenario(scenario);

    if (scenario === "geoFail") {
      throw apiError(503, "geoUnavailable", "Mock: geolocation unavailable");
    }
    // ?ctry=XX, parsed by the same helper the real implementation uses.
    const forced = getForcedCountryCode();
    if (forced === "MX") return GEO_MX;
    if (forced === "CA") return GEO_CA;
    // No city fixture for other countries — the account defaults fall back to
    // asking, exactly as they do when the real lookup returns country only.
    if (forced) return { countryCode: forced };
    return GEO_CA;
  },

  async getCityOptions(): Promise<IBreakdownCityDto[]> {
    logCall("getCityOptions", {});
    await applySharedScenario(getMockScenario());
    return CITY_OPTIONS.slice();
  },

  async getHome(): Promise<ISubmissionHomeDto> {
    logCall("getHome", {});
    await applySharedScenario(getMockScenario());
    const session = requireAuthenticatedSession();
    return mockHomeOrAsk(session);
  },

  // ?mockScenario=classicSession stands for the classic site's cookie: an existing actor logged in
  // there. It stays for the tab once used, as the cookie would (step 1 hides it after Log out).
  async findClassicSession(): Promise<IClassicSessionDto | null> {
    logCall("findClassicSession", {});
    await applySharedScenario(getMockScenario());
    const present = getMockScenario() === "classicSession" || readMockFlag(MOCK_CLASSIC_KEY);
    return present ? { ...MOCK_CLASSIC_ACTOR } : null;
  },

  // "Continue as": the demo actor, made through the public seam as the mock login does, and an
  // existing account from then on (their library, no film center or age statement), whatever
  // ?mockScenario= the address still carries.
  async continueClassicSession(): Promise<ITabSignInDto> {
    logCall("continueClassicSession", {});
    const query = new URLSearchParams(window.location.search);
    writeMockFlag(MOCK_CLASSIC_KEY);
    resetMockBackend();
    const geo = await this.getRequesterLocation();
    const start = await this.startSignup({
      firstName: MOCK_CLASSIC_ACTOR.firstName,
      lastName: MOCK_CLASSIC_ACTOR.lastName,
      email: "paola.demo@theworkbook.com",
      password: "abc123",
      recaptchaToken: "mock-classic-session",
      breakdownId: query.get("bd") || undefined,
      roleId: query.get("roleid") || undefined,
      geoCountryCode: geo.countryCode || undefined,
      geoRegionCode: geo.regionCode,
      geoCityId: geo.cityId,
    });
    const verified = await this.verifyEmailCode(start.sessionId, MOCK_EMAIL_CODE);
    const session = readMockSession();
    if (session) writeMockSession({ ...session, existingAccount: true });
    return {
      clientId: verified.clientId,
      jwt: verified.jwt,
      firstName: MOCK_CLASSIC_ACTOR.firstName,
      lastName: MOCK_CLASSIC_ACTOR.lastName,
    };
  },

  async getAccountName(): Promise<IAccountNameDto | null> {
    logCall("getAccountName", {});
    const session = readMockSession();
    if (!session || !session.verified || !session.firstName) return null;
    return { firstName: session.firstName, lastName: session.lastName || "" };
  },

  async setHomeLocation(payload: ISetHomeLocationRequest): Promise<IHomeLocationDto> {
    logCall("setHomeLocation", payload);
    await applySharedScenario(getMockScenario());

    // Authenticated: the account exists by the time the gate can appear.
    const session = readMockSession();
    if (!session || !session.verified || !session.jwt) {
      throw apiError(401, "unauthorized", "Mock: sign in first");
    }

    const countryCode = (payload.countryCode || "").trim().toUpperCase();
    const known = CITY_OPTIONS.some(
      (c) => (c.countryCode || "").toUpperCase() === countryCode
    );
    if (!countryCode || !known) {
      throw apiError(400, "invalidCountry", "Mock: unsupported country");
    }

    // As the real one: both, the country and a city in it (decided 2026-10-29).
    if (!payload.cityId) {
      throw apiError(400, "invalidCity", "Mock: choose a city in that country");
    }
    const city = CITY_OPTIONS.filter((c) => c.cityId === payload.cityId)[0];
    // A city from a different country is a client bug, not a user choice.
    if (!city || (city.countryCode || "").toUpperCase() !== countryCode) {
      throw apiError(400, "invalidCity", "Mock: city is not in that country");
    }

    const previousCountry = (session.homeCountryCode || session.geoCountryCode || "")
      .toUpperCase();
    // Two writes in the real API — the account's residence and the session
    // record. One mock store stands in for both.
    writeMockSession({
      ...session,
      homeCountryCode: countryCode,
      homeCityId: city.cityId,
      homeConfirmed: true,
    });

    return {
      countryCode,
      cityId: city.cityId,
      cityName: city.cityName,
      provinceId: city.provinceId,
      // Changing country changes plans; the flag tells Step 4 to re-price.
      planRegionChanged: !!previousCountry && previousCountry !== countryCode,
    };
  },

  // -------------------------------------------------------------------------
  // Step 1 — form submit. Validates + sends the code. NO account created here.
  // -------------------------------------------------------------------------

  async checkEmailAvailable(email: string): Promise<{ available: boolean }> {
    const scenario = getMockScenario();
    logCall("checkEmailAvailable", { email });
    await applySharedScenario(scenario);
    // Magic address for demoing the "already registered" branch.
    return { available: !isTakenEmail(email) };
  },

  async startSignup(payload: IStartSignupRequest): Promise<IStartSignupResponse> {
    const scenario = getMockScenario();
    logCall("startSignup", { ...payload, password: "***", recaptchaToken: "***" });
    await applySharedScenario(scenario);

    if (payload.information) {
      // Honeypot filled — a bot. Real backend answers 403 and force-completes.
      throw apiError(403, "honeypotTriggered", "Mock: honeypot triggered");
    }
    if (!payload.firstName || !payload.lastName) {
      throw apiError(400, "missingName", "Mock: first and last name are required");
    }
    if (!isPlausibleEmail(payload.email)) {
      throw apiError(400, "invalidEmail", "Mock: email is not valid");
    }
    if (!isStrongEnough(payload.password)) {
      throw apiError(400, "weakPassword", "Mock: password does not meet the rule");
    }
    if (isTakenEmail(payload.email)) {
      throw apiError(409, "emailAlreadyRegistered", "Mock: email already has an account");
    }
    // A real backend leaves an existing account untouched here; the mock must
    // not recreate its single session record over a verified one, or every
    // later authenticated call would 401 with no way back.
    const existing = readMockSession();
    if (existing && existing.verified) {
      throw apiError(
        409,
        "emailAlreadyRegistered",
        "Mock: an account was already created in this tab"
      );
    }
    if (!payload.recaptchaToken) {
      throw apiError(400, "recaptchaFailed", "Mock: missing reCAPTCHA token");
    }
    if (payload.recaptchaToken === "mock-low-score") {
      throw apiError(403, "recaptchaLowScore", "Mock: reCAPTCHA score too low");
    }

    const session = createMockSession({
      firstName: payload.firstName,
      lastName: payload.lastName,
      email: payload.email,
      breakdownId: payload.breakdownId,
      roleId: payload.roleId,
      geoCountryCode: payload.geoCountryCode,
      geoRegionCode: payload.geoRegionCode,
      geoCityId: payload.geoCityId,
    });
    // eslint-disable-next-line no-console
    console.info(
      `[mock-email] verification code for ${payload.email}: ${MOCK_EMAIL_CODE}`
    );
    pushOutbox("verification-code", payload.email, `Your code is ${MOCK_EMAIL_CODE}`);

    return {
      sessionId: session.sessionId,
      maskedEmail: maskEmail(session.email),
      resendCooldownSeconds: EMAIL_RESEND_COOLDOWN_S,
      codeExpiresOnUtc: session.codeExpiresOnUtc,
      serverTimeUtc: new Date().toISOString(),
    };
  },

  // -------------------------------------------------------------------------
  // Step 1b — verification. THIS is where the account is created.
  // -------------------------------------------------------------------------

  async verifyEmailCode(sessionId: string, code: string): Promise<IVerifyEmailResponse> {
    const scenario = getMockScenario();
    logCall("verifyEmailCode", { sessionId, code });
    await applySharedScenario(scenario);

    const session = requireSession(sessionId);

    if (session.verified) {
      throw apiError(409, "sessionAlreadyVerified", "Mock: session already verified");
    }
    if (Date.parse(session.codeExpiresOnUtc) <= Date.now()) {
      throw apiError(410, "codeExpired", "Mock: code expired");
    }
    if (session.attemptsRemaining <= 0) {
      throw apiError(429, "tooManyAttempts", "Mock: too many attempts", 300);
    }
    if (code !== session.code) {
      session.attemptsRemaining -= 1;
      writeMockSession(session);
      throw apiError(
        400,
        session.attemptsRemaining <= 0 ? "tooManyAttempts" : "invalidCode",
        `Mock: invalid code (${session.attemptsRemaining} attempts left)`,
        session.attemptsRemaining <= 0 ? 300 : undefined
      );
    }

    // The city the account is created with: geolocated city, else the first in
    // the same province, else the first in the country. `source` says which
    // rung answered, so the flow can tell a precise city from a country-level
    // guess worth letting the user correct.
    const resolved = resolveAccountCity({
      cityId: session.geoCityId,
      regionCode: session.geoRegionCode,
      countryCode: session.geoCountryCode,
    });

    // Account creation with the auto-applied defaults. The resolved city is
    // mirrored onto the session record as well as the account, so `state` and
    // the tracking payload never keep answering with the raw IP guess.
    session.verified = true;
    session.clientId = 9000000 + Math.floor(Math.random() * 99999);
    session.jwt = `Bearer mock.jwt.${session.clientId}`;
    session.homeCityId = resolved.city && resolved.city.cityId;
    session.homeCountryCode =
      (resolved.city && resolved.city.countryCode) || session.geoCountryCode;
    session.geoSource = resolved.source;
    writeMockSession(session);

    pushOutbox("welcome", session.email, "Welcome to theWorkbook");
    return {
      sessionId: session.sessionId,
      clientId: session.clientId,
      jwt: session.jwt,
      refreshToken: "mock-refresh-token",
      expiresIn: 3600,
      accountDefaults: {
        cityId: resolved.city && resolved.city.cityId,
        cityName: resolved.city && resolved.city.cityName,
        countryCode: (resolved.city && resolved.city.countryCode) || session.geoCountryCode,
        regionCode: resolved.city && resolved.city.regionCode,
        geoSource: resolved.source,
        selfRepresented: true,
        agencyType: 65,
        sendBreakdowns: true,
      },
      welcomeEmailQueued: true,
    };
  },

  async resendEmailCode(sessionId: string): Promise<IResendCodeResponse> {
    const scenario = getMockScenario();
    logCall("resendEmailCode", { sessionId });
    await applySharedScenario(scenario);

    const session = requireSession(sessionId);
    const now = Date.now();

    if (Date.parse(session.resendAvailableAtUtc) > now) {
      const retryAfter = Math.ceil(
        (Date.parse(session.resendAvailableAtUtc) - now) / 1000
      );
      throw apiError(429, "resendCooldown", "Mock: still cooling down", retryAfter);
    }
    if (session.sendsRemaining <= 0) {
      throw apiError(429, "sendLimit", "Mock: send limit reached", 900);
    }

    session.sendsRemaining -= 1;
    session.code = MOCK_EMAIL_CODE;
    // Regenerated codes get the shorter TTL, like EmailVerificationService.
    session.codeExpiresOnUtc = isoIn.minutes(30);
    session.resendAvailableAtUtc = isoIn.seconds(EMAIL_RESEND_COOLDOWN_S);
    // A resend restores the guess budget — sends and guesses are independent.
    session.attemptsRemaining = MAX_VERIFY_ATTEMPTS;
    writeMockSession(session);
    pushOutbox("verification-code", session.email, `Your code is ${MOCK_EMAIL_CODE}`);

    return {
      sent: true,
      resendCooldownSeconds: EMAIL_RESEND_COOLDOWN_S,
      codeExpiresOnUtc: session.codeExpiresOnUtc,
      sendsRemaining: session.sendsRemaining,
      serverTimeUtc: new Date().toISOString(),
    };
  },

  async getSignupState(sessionId: string): Promise<ISignupStateResponse> {
    const scenario = getMockScenario();
    logCall("getSignupState", { sessionId });
    await applySharedScenario(scenario);

    const session = requireSession(sessionId);
    return {
      sessionId: session.sessionId,
      maskedEmail: maskEmail(session.email),
      verified: session.verified,
      resendAvailableAtUtc: session.resendAvailableAtUtc,
      codeExpiresOnUtc: session.codeExpiresOnUtc,
      attemptsRemaining: session.attemptsRemaining,
      serverTimeUtc: new Date().toISOString(),
      breakdownId: session.breakdownId || undefined,
      roleId: session.roleId || undefined,
      homeCountryCode: session.homeCountryCode,
      homeCityId: session.homeCityId,
    };
  },

  // -------------------------------------------------------------------------
  // Step 2 — materials
  // -------------------------------------------------------------------------

  async createDraft(
    breakdownId: string | null,
    roleId: string | null
  ): Promise<ISubmissionDraftDto> {
    const scenario = getMockScenario();
    logCall("createDraft", { breakdownId, roleId });
    await applySharedScenario(scenario);
    // The demo shortcut (mock-seed) walks through here too; only the flow's own pages refuse.
    if (scenario === "alreadySubmitted" && breakdownId && window.location.pathname.indexOf("mock-seed") < 0) {
      throw apiError(409, "alreadySubmitted", "Mock: this account already submitted to this casting call");
    }

    const session = requireAuthenticatedSession();
    if (!session.submissionId) {
      session.submissionId = 7000000 + Math.floor(Math.random() * 99999);
      session.breakdownId = breakdownId;
      session.roleId = roleId;
      writeMockSession(session);
    }
    return toDraft(session);
  },

  async uploadMedia(payload: IUploadMediaRequest): Promise<IMediaAssetDto> {
    const scenario = getMockScenario();
    logCall("uploadMedia", payload);
    await applySharedScenario(scenario);

    requireAuthenticatedSession();

    const maxBytes = payload.kind === "photo" ? 10 * 1024 * 1024 : 5 * 1024 * 1024;
    if (payload.sizeBytes > maxBytes) {
      throw apiError(
        413,
        "fileTooLarge",
        `Mock: ${payload.kind} exceeds ${Math.round(maxBytes / 1024 / 1024)}MB`
      );
    }
    // The four formats the editor can decode. It always emits JPEG, so this is
    // really a guard for anything bypassing the editor.
    const allowed =
      payload.kind === "photo"
        ? ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/bmp"]
        : ["application/pdf"];
    if (allowed.indexOf(payload.mimeType) === -1) {
      throw apiError(415, "unsupportedFileType", `Mock: ${payload.mimeType} not allowed`);
    }

    const session = readMockSession();
    const assetId = 5000000 + Math.floor(Math.random() * 99999);
    if (session) {
      if (payload.kind === "photo") session.photoAssetId = assetId;
      else session.resumeAssetId = assetId;
      writeMockSession(session);
    }

    // Keep the bytes. A real upload leaves them somewhere that outlives the
    // page; without this the mock's `url` was always undefined and every screen
    // after step 2 showed a grey box where the headshot should be.
    return putMockAsset(
      {
        assetId,
        kind: payload.kind,
        fileName: payload.fileName,
        sizeBytes: payload.sizeBytes,
        uploadedAtUtc: new Date().toISOString(),
      },
      payload.file
    );
  },

  async getExistingMedia(kind: MediaKind): Promise<IMediaAssetDto[]> {
    const scenario = getMockScenario();
    logCall("getExistingMedia", { kind });
    await applySharedScenario(scenario);
    // A brand-new golden-path account has only what it uploaded in this tab (a
    // reload on step 2 finds it, as PM would). An existing user has a library.
    if (!mockNewSignup()) return MOCK_LIBRARY.filter((a) => a.kind === kind);
    return listMockAssets(kind);
  },

  async saveMaterials(
    submissionId: number,
    payload: ISaveMaterialsRequest
  ): Promise<ISubmissionDraftDto> {
    const scenario = getMockScenario();
    logCall("saveMaterials", { submissionId, ...payload });
    await applySharedScenario(scenario);

    const session = requireAuthenticatedSession();
    // As the real one: an existing user isn't asked, the account cleared it.
    if (!payload.ageAttested && mockNewSignup()) {
      throw apiError(400, "ageAttestationRequired", "Mock: age attestation required");
    }
    if (mustChooseHome(session)) {
      throw apiError(400, "homeLocationRequired", "Mock: choose your country and film center");
    }
    if (!payload.photoAssetId) {
      throw apiError(400, "photoRequired", "Mock: a photo is required to submit");
    }
    if (!payload.resumeAssetId && !(payload.noteToCastingDirector || "").trim()) {
      throw apiError(400, "resumeOrNoteRequired", "Mock: add a resume or a note to casting");
    }

    session.photoAssetId = payload.photoAssetId || session.photoAssetId;
    session.resumeAssetId = payload.resumeAssetId || session.resumeAssetId;
    session.note = payload.noteToCastingDirector || "";
    session.ageAttested = payload.ageAttested || !mockNewSignup();
    writeMockSession(session);
    return toDraft(session);
  },

  async saveForLater(submissionId: number, payload: ISaveForLaterRequest): Promise<ISaveForLaterResponse> {
    const scenario = getMockScenario();
    logCall("saveForLater", { submissionId, ...payload });
    await applySharedScenario(scenario);

    // As the real one: what the page has, nothing required, the step left open.
    const session = requireAuthenticatedSession();
    const note = (payload.noteToCastingDirector || "").trim();
    if (note.length > 600) throw apiError(400, "noteTooLong", "Mock: the note is too long");
    session.photoAssetId = payload.photoAssetId || null;
    session.resumeAssetId = payload.resumeAssetId || null;
    session.note = note;
    session.ageAttested = session.ageAttested || payload.ageAttested || !mockNewSignup();

    let emailStatus: FinishLaterEmailStatus = "notRequested";
    if (payload.sendEmail) {
      const last = session.finishLaterEmailAt ? Date.parse(session.finishLaterEmailAt) : 0;
      if (last && Date.now() - last < 24 * 3600 * 1000) emailStatus = "alreadySentToday";
      else {
        emailStatus = "sent";
        session.finishLaterEmailAt = new Date().toISOString();
      }
    }
    writeMockSession(session);
    return { draft: toDraft(session), emailStatus };
  },

  // -------------------------------------------------------------------------
  // Step 3 — phone verification
  // -------------------------------------------------------------------------

  async getPhoneStatus(): Promise<IPhoneStatusDto> {
    const scenario = getMockScenario();
    logCall("getPhoneStatus", {});
    await applySharedScenario(scenario);
    const session = requireAuthenticatedSession();
    return { isVerified: session.phoneVerified, phone: session.phone ? `+${session.phone}` : undefined };
  },

  async recordPhone(submissionId: number): Promise<ISubmissionDraftDto> {
    const scenario = getMockScenario();
    logCall("recordPhone", { submissionId });
    await applySharedScenario(scenario);
    const session = requireAuthenticatedSession();
    if (!session.submissionId || session.submissionId !== submissionId) {
      throw apiError(404, "submissionNotFound", "Mock: no such submission");
    }
    // As the real one: "Add materials later" can store everything but the film center.
    if (mustChooseHome(session)) {
      throw apiError(409, "homeLocationRequired", "Mock: choose your country and film center first");
    }
    if (!session.phoneVerified) throw apiError(409, "phoneNotVerified", "Mock: phone not verified");
    return toDraft(session);
  },

  async sendPhoneCode(payload: ISendPhoneCodeRequest): Promise<ISendPhoneCodeResponse> {
    const scenario = getMockScenario();
    logCall("sendPhoneCode", payload);
    await applySharedScenario(scenario);

    const session = requireAuthenticatedSession();
    const digits = (payload.phone || "").replace(/[^0-9]/g, "");
    if (digits.length < 8 || digits.length > 15) {
      throw apiError(400, "invalidPhone", "Mock: phone number is not valid");
    }
    // Magic number for the "already in use" branch.
    if (digits.indexOf("5555555") === 0) {
      throw apiError(409, "phoneAlreadyInUse", "Mock: phone already verified on another account");
    }
    if (session.phoneSendsRemaining <= 0) {
      throw apiError(429, "sendLimit", "Mock: SMS send limit reached", 900);
    }
    if (
      session.phoneResendAvailableAtUtc &&
      Date.parse(session.phoneResendAvailableAtUtc) > Date.now()
    ) {
      const retryAfter = Math.ceil(
        (Date.parse(session.phoneResendAvailableAtUtc) - Date.now()) / 1000
      );
      throw apiError(429, "resendCooldown", "Mock: still cooling down", retryAfter);
    }

    session.phone = digits;
    session.phoneCode = MOCK_PHONE_CODE;
    session.phoneCodeExpiresOnUtc = isoIn.minutes(PHONE_CODE_TTL_MIN);
    session.phoneResendAvailableAtUtc = isoIn.seconds(PHONE_RESEND_COOLDOWN_S);
    session.phoneSendsRemaining -= 1;
    session.phoneAttemptsRemaining = MAX_VERIFY_ATTEMPTS;
    writeMockSession(session);

    // eslint-disable-next-line no-console
    console.info(`[mock-sms] code for +${digits}: ${MOCK_PHONE_CODE}`);
    pushOutbox("sms-code", `+${digits}`, `Your code is ${MOCK_PHONE_CODE}`);

    return {
      sent: true,
      maskedPhone: maskPhone(digits),
      resendCooldownSeconds: PHONE_RESEND_COOLDOWN_S,
      codeExpiresOnUtc: session.phoneCodeExpiresOnUtc,
      sendsRemaining: session.phoneSendsRemaining,
      serverTimeUtc: new Date().toISOString(),
    };
  },

  async verifyPhoneCode(code: string, _options?: IVerifyPhoneOptions): Promise<IVerifyPhoneResponse> {
    const scenario = getMockScenario();
    logCall("verifyPhoneCode", { code });
    await applySharedScenario(scenario);

    const session = requireAuthenticatedSession();
    if (!session.phoneCode || !session.phoneCodeExpiresOnUtc) {
      throw apiError(400, "invalidCode", "Mock: no code was sent");
    }
    if (Date.parse(session.phoneCodeExpiresOnUtc) <= Date.now()) {
      throw apiError(410, "codeExpired", "Mock: phone code expired");
    }
    if (session.phoneAttemptsRemaining <= 0) {
      throw apiError(429, "tooManyAttempts", "Mock: too many attempts", 300);
    }
    if (code !== session.phoneCode) {
      session.phoneAttemptsRemaining -= 1;
      writeMockSession(session);
      throw apiError(
        400,
        session.phoneAttemptsRemaining <= 0 ? "tooManyAttempts" : "invalidCode",
        `Mock: invalid code (${session.phoneAttemptsRemaining} attempts left)`
      );
    }

    session.phoneVerified = true;
    writeMockSession(session);
    return { verified: true, phoneConfirmedAtUtc: new Date().toISOString() };
  },

  // -------------------------------------------------------------------------
  // Step 4 — membership, review, paywall, checkout, send
  // -------------------------------------------------------------------------

  async getSubmissionMembership(): Promise<ISubmissionMembershipDto> {
    const scenario = getMockScenario();
    logCall("getSubmissionMembership", {});
    await applySharedScenario(scenario);
    return membershipFor(requireAuthenticatedSession(), scenario);
  },

  async getSubmissionReview(submissionId: number): Promise<ISubmissionReviewDto> {
    const scenario = getMockScenario();
    logCall("getSubmissionReview", { submissionId });
    await applySharedScenario(scenario);

    const session = requireAuthenticatedSession();
    if (!session.submissionId || session.submissionId !== submissionId) {
      throw apiError(404, "sessionNotFound", "Mock: no such submission");
    }
    const info = breakdownFixture(session.breakdownId || "", session.roleId || undefined);
    const draft = toDraft(session);
    const city = resolveAccountCity({
      cityId: session.homeCityId || session.geoCityId,
      regionCode: session.geoRegionCode,
      countryCode: session.homeCountryCode || session.geoCountryCode,
    });

    return {
      submissionId,
      breakdownId: session.breakdownId,
      breakdownName: info.breakdownName,
      projectType: info.projectType,
      roleId: session.roleId,
      roleName: info.selectedRole ? info.selectedRole.roleName : undefined,
      actorName: `${session.firstName} ${session.lastName}`.trim(),
      // Figma shows "5'8'' - 150 LB - Vancouver", but this flow never asks for
      // height or weight — so the line carries only what the account actually
      // knows. It grows when a profile step exists to fill it.
      statsLine: city.city ? city.city.cityName : undefined,
      photo: draft.photo,
      resume: draft.resume,
      noteToCastingDirector: session.note || undefined,
      representation: representationFor(session),
      // Stands in for the breakdown's public page, which lives on the classic
      // site and is not this app's to route to. The boost landing page renders
      // the same breakdown from the same fixture, so a demo click opens
      // something real rather than a guessed .asp path that 404s.
      breakdownDetailsUrl: session.breakdownId
        ? `/submission/boost?bd=${encodeURIComponent(session.breakdownId)}${
            session.roleId ? `&roleid=${encodeURIComponent(session.roleId)}` : ""
          }`
        : undefined,
      status: session.submissionStatus,
      closingDateUtc: info.closingDateUtc,
      serverTimeUtc: new Date().toISOString(),
      membership: membershipFor(session, scenario),
      // Offered to new signups only: every mock account is one, but ?mockScenario=existingUser.
      jobAlerts: { offered: mockNewSignup(), subscribed: session.jobAlerts === true, declined: session.jobAlerts === false },
    };
  },

  async searchAgencies(query: string): Promise<IAgencyOptionDto[]> {
    logCall("searchAgencies", { query });
    await applySharedScenario(getMockScenario());
    requireAuthenticatedSession();

    const needle = (query || "").trim().toLowerCase();
    // Below two characters every agency matches, which is a list, not a search.
    if (needle.length < 2) return [];
    return AGENCY_OPTIONS.filter(
      (a) => a.agencyName.toLowerCase().indexOf(needle) >= 0
    ).slice(0, 6);
  },

  // Any token works except one with "bad" in it, which is how the mock shows a broken link.
  async unsubscribeWinBack(token: string): Promise<IUnsubscribeResponse> {
    logCall("unsubscribeWinBack", { token: token ? "***" : "" });
    await applySharedScenario(getMockScenario());
    if (!token || /bad/i.test(token)) throw apiError(400, "invalidLink", "Mock: this link doesn't work");
    return { unsubscribed: true, alreadyUnsubscribed: false };
  },

  async winBackReturn(eref: string): Promise<void> {
    // The server stamps the email's return; the mock has nothing to stamp.
    logCall("winBackReturn", { eref: eref ? "***" : "" });
  },

  async listAgencyAgents(agencyId: number): Promise<IAgentOptionDto[]> {
    logCall("listAgencyAgents", { agencyId });
    await applySharedScenario(getMockScenario());
    requireAuthenticatedSession();
    return AGENCY_AGENTS[agencyId] || [];
  },

  async setJobAlerts(subscribe: boolean): Promise<void> {
    const scenario = getMockScenario();
    logCall("setJobAlerts", { subscribe });
    await applySharedScenario(scenario);
    const session = requireAuthenticatedSession();
    writeMockSession({ ...session, jobAlerts: subscribe });
  },

  async setRepresentation(
    submissionId: number,
    payload: ISetRepresentationRequest
  ): Promise<IRepresentationDto> {
    logCall("setRepresentation", payload);
    await applySharedScenario(getMockScenario());

    const session = requireAuthenticatedSession();
    if (!session.submissionId || session.submissionId !== submissionId) {
      throw apiError(404, "sessionNotFound", "Mock: no such submission");
    }

    if (payload.type === "agency") {
      const agency = AGENCY_OPTIONS.filter((a) => a.agencyId === payload.agencyId)[0];
      if (!agency) {
        throw apiError(400, "invalidRepresentation", "Mock: unknown agency");
      }
      const listed = (AGENCY_AGENTS[agency.agencyId] || []).filter((a) => a.agentId === payload.agentId)[0];
      // Not from the list: ProfileManagement needs the agent's name, email and phone.
      if (!listed && (!(payload.agentName || "").trim() || !isPlausibleEmail((payload.agentEmail || "").trim()) || !(payload.agentPhone || "").trim())) {
        throw apiError(400, "invalidRepresentation", "Mock: a new agent needs a name, email and phone");
      }
      if (!listed && agency.allowNewAgents === false) {
        throw apiError(409, "agentNotAdded", "Mock: this agency takes no new agents from here");
      }
      session.representationType = "agency";
      session.representationAgencyId = agency.agencyId;
      session.representationAgencyName = agency.agencyName;
      session.representationAgentName = listed ? listed.agentName : (payload.agentName || "").trim() || null;
      session.representationInvitedEmail = null;
    } else if (payload.type === "invited") {
      // Inviting an agent who is not in the system needs somewhere to send it.
      const email = (payload.agentEmail || "").trim();
      const agencyName = (payload.agencyName || "").trim();
      if (!agencyName || !(payload.agentName || "").trim() || !isPlausibleEmail(email) || !(payload.agentPhone || "").trim()) {
        throw apiError(
          400,
          "invalidRepresentation",
          "Mock: an invite needs the agency, and the agent's name, email and phone"
        );
      }
      session.representationType = "invited";
      session.representationAgencyId = null;
      session.representationAgencyName = agencyName;
      session.representationAgentName = (payload.agentName || "").trim() || null;
      session.representationInvitedEmail = email;
      pushOutbox("agentInvite", email, `${session.firstName} invited you to theWorkbook`);
    } else {
      session.representationType = "selfRepresented";
      session.representationAgencyId = null;
      session.representationAgencyName = null;
      session.representationAgentName = null;
      session.representationInvitedEmail = null;
    }

    writeMockSession(session);
    return representationFor(session);
  },

  async getPaywallOffer(variant: Step4Variant): Promise<IPaywallOfferDto> {
    const scenario = getMockScenario();
    logCall("getPaywallOffer", { variant });
    await applySharedScenario(scenario);

    const session = requireAuthenticatedSession();
    return buildPaywallOffer(variant, accountCountry(session));
  },

  async startCheckout(payload: IStartCheckoutRequest): Promise<IStartCheckoutResponse> {
    const scenario = getMockScenario();
    logCall("startCheckout", payload);
    await applySharedScenario(scenario);

    const session = requireAuthenticatedSession();
    const membership = membershipFor(session, scenario);
    // Nobody who is already entitled should be able to buy again — the screens
    // route around this, but the endpoint refuses it too.
    if (membership.membershipStatus === "pro") {
      throw apiError(409, "alreadyPro", "Mock: this account is already PRO");
    }
    if (membership.membershipStatus === "trial") {
      throw apiError(409, "alreadyPro", "Mock: a trial is already active");
    }
    if (!membership.trialAvailable && !membership.freeMarket) {
      throw apiError(403, "notEligible", "Mock: not eligible for a trial");
    }

    const offer = buildPaywallOffer(payload.variant, accountCountry(session));
    if (!offer.services.some((s) => s.serviceId === payload.serviceId)) {
      throw apiError(400, "notEligible", "Mock: unknown service for this market");
    }

    const checkoutSessionId = `cs_mock_${Date.now().toString(36)}`;
    session.serviceId = payload.serviceId;
    session.checkoutSessionId = checkoutSessionId;
    session.attemptId = 50000 + Math.floor(Math.random() * 9999);
    session.checkoutStatus = "open";
    session.checkoutPolls = 0;
    writeMockSession(session);

    return {
      // A real backend returns checkout.stripe.com; the mock hands back an
      // in-app page so the redirect is a real full-page navigation either way.
      redirectUrl: `/submission/checkout?session_id=${encodeURIComponent(
        checkoutSessionId
      )}&return=${encodeURIComponent(payload.returnPath || "")}`,
      checkoutSessionId,
      attemptId: session.attemptId,
      // Stripe expires a session after 60 minutes.
      expiresAtUtc: isoIn.minutes(60),
    };
  },

  async getCheckoutStatus(checkoutSessionId: string): Promise<ICheckoutStatusDto> {
    const scenario = getMockScenario();
    logCall("getCheckoutStatus", { checkoutSessionId });
    await applySharedScenario(scenario);

    const session = requireAuthenticatedSession();
    if (!session.checkoutSessionId || session.checkoutSessionId !== checkoutSessionId) {
      throw apiError(404, "checkoutSessionNotFound", "Mock: unknown checkout session");
    }
    if (session.checkoutStatus === "expired" || scenario === "sessionExpired") {
      throw apiError(410, "checkoutSessionExpired", "Mock: checkout session expired");
    }

    if (session.checkoutStatus !== "complete" || scenario === "webhookSlow") {
      return {
        checkoutSessionId,
        status: "open",
        membershipStatus: session.membershipStatus,
        attemptId: session.attemptId || undefined,
      };
    }

    // Paid, but the webhook that grants the membership has not landed yet.
    // Two polls of lag, so the return page's polling is exercised instead of
    // being short-circuited by an instant answer.
    //
    // `<=` after a pre-increment, so the count is the number of "open" answers
    // actually given: with `<` the constant said two and the mock delivered
    // one, and a lag of one poll is thin cover for a loop with eight delays in
    // it.
    session.checkoutPolls += 1;
    if (session.checkoutPolls <= WEBHOOK_LAG_POLLS) {
      writeMockSession(session);
      return {
        checkoutSessionId,
        status: "open",
        membershipStatus: session.membershipStatus,
        attemptId: session.attemptId || undefined,
      };
    }

    // The webhook: this is where entitlement is actually granted.
    session.membershipStatus = "trial";
    session.trialUsed = true;
    writeMockSession(session);
    return {
      checkoutSessionId,
      status: "complete",
      membershipStatus: session.membershipStatus,
      attemptId: session.attemptId || undefined,
    };
  },

  async completeMockCheckout(
    checkoutSessionId: string,
    outcome: MockCheckoutOutcome
  ): Promise<ICheckoutStatusDto> {
    const scenario = getMockScenario();
    logCall("completeMockCheckout", { checkoutSessionId, outcome });
    await applySharedScenario(scenario);

    const session = requireAuthenticatedSession();
    if (!session.checkoutSessionId || session.checkoutSessionId !== checkoutSessionId) {
      throw apiError(404, "checkoutSessionNotFound", "Mock: unknown checkout session");
    }

    // A scenario flag forces the outcome, so a bookmarked demo link produces a
    // decline without anyone remembering to pick it on the page.
    const effective: MockCheckoutOutcome =
      scenario === "cardDeclined"
        ? "card_declined"
        : scenario === "sessionExpired"
        ? "session_expired"
        : outcome;

    if (effective === "card_declined") {
      const error = apiError(402, "cardDeclined", "Mock: your card was declined");
      (error as any).declineCode = "generic_decline";
      throw error;
    }
    if (effective === "session_expired") {
      session.checkoutStatus = "expired";
      writeMockSession(session);
      throw apiError(410, "checkoutSessionExpired", "Mock: checkout session expired");
    }
    if (effective === "abandoned_3ds") {
      // Nothing was charged and nothing is granted: the session stays open,
      // which is what stops an abandoned 3DS from reading as success.
      return {
        checkoutSessionId,
        status: "open",
        membershipStatus: session.membershipStatus,
        attemptId: session.attemptId || undefined,
      };
    }

    session.checkoutStatus = "complete";
    session.checkoutPolls = 0;
    writeMockSession(session);
    pushOutbox("receipt", session.email, "Your theWorkbook PRO trial has started");
    return {
      checkoutSessionId,
      // Still "open" to the caller: the membership is granted by the webhook,
      // which getCheckoutStatus simulates a couple of polls later.
      status: "open",
      membershipStatus: session.membershipStatus,
      attemptId: session.attemptId || undefined,
    };
  },

  async sendSubmission(
    submissionId: number,
    payload: ISendSubmissionRequest
  ): Promise<ISendSubmissionResponse> {
    const scenario = getMockScenario();
    logCall("sendSubmission", { submissionId, ...payload });
    await applySharedScenario(scenario);

    const session = requireAuthenticatedSession();
    if (!session.submissionId || session.submissionId !== submissionId) {
      throw apiError(404, "sessionNotFound", "Mock: no such submission");
    }

    // Idempotency first, before any other check: a retry after a lost response
    // must return the original result rather than a 409 the caller has to
    // interpret, or worse a second submission.
    if (
      session.sentClientRequestId &&
      session.sentClientRequestId === payload.clientRequestId
    ) {
      return sentResponse(session);
    }
    if (session.submissionStatus === "Sent") {
      throw apiError(409, "alreadySent", "Mock: this submission was already sent");
    }

    // No message, so the page shows its own sentence rather than the mock's.
    if (scenario === "sendFails") {
      throw apiError(500, "serverError", "");
    }

    const info = breakdownFixture(session.breakdownId || "", session.roleId || undefined);
    if (isForeignRole(info, session.roleId)) {
      throw apiError(409, "roleNotFound", "Mock: this role isn't part of this casting call");
    }
    if (isClosedBreakdown(info)) {
      throw apiError(409, "breakdownClosed", "Mock: this casting call has closed");
    }
    if (!session.photoAssetId) {
      throw apiError(422, "photoRequired", "Mock: every submission needs a photo");
    }

    // The gate. Server-side, because a client that decides its own entitlement
    // is a client that can be edited.
    const membership = membershipFor(session, scenario);
    // Not placed and still free: step 2's film center first. On a plan, the plan set the country.
    const onAPlan = membership.membershipStatus === "pro" || membership.membershipStatus === "trial";
    if (!onAPlan && mustChooseHome(session)) {
      throw apiError(409, "homeLocationRequired", "Mock: choose your country and film center first");
    }
    if (!membership.canSubmitFree) {
      throw apiError(
        402,
        "membershipRequired",
        "Only PRO members or Trial users can submit to roles."
      );
    }
    // After the plan, as the server checks: the account's country isn't one casting takes (its
    // home changed since step 2, say). No message: the page has its own.
    if (scenario === "countryRefused") {
      throw apiError(403, "countryNotEligible", "");
    }

    session.submissionStatus = "Sent";
    session.sentOnUtc = new Date().toISOString();
    session.sentClientRequestId = payload.clientRequestId;
    writeMockSession(session);
    pushOutbox("submissionSent", session.email, `Submitted to ${info.breakdownName}`);
    return sentResponse(session);
  },

  async holdSubmission(
    submissionId: number,
    payload: IHoldSubmissionRequest
  ): Promise<IHoldSubmissionDto> {
    const scenario = getMockScenario();
    logCall("holdSubmission", { submissionId, ...payload });
    await applySharedScenario(scenario);

    const session = requireAuthenticatedSession();
    if (!session.submissionId || session.submissionId !== submissionId) {
      throw apiError(404, "sessionNotFound", "Mock: no such submission");
    }
    if (session.submissionStatus === "Sent") {
      throw apiError(409, "alreadySent", "Mock: this submission was already sent");
    }

    const info = breakdownFixture(session.breakdownId || "", session.roleId || undefined);
    if (isForeignRole(info, session.roleId)) {
      throw apiError(409, "roleNotFound", "Mock: this role isn't part of this casting call");
    }
    if (isClosedBreakdown(info)) {
      throw apiError(409, "breakdownClosed", "Mock: this casting call has closed");
    }

    session.submissionStatus = "Saved";
    // The hold is the breakdown's own deadline: we cannot keep it past the
    // point where sending it would do anything.
    // Past the deadline (late) the hold promises no date, as the API: the
    // margin's end is never shown.
    session.holdUntilUtc = info.isLate ? undefined : info.closingDateUtc;
    // Idempotent: asking twice must not schedule a second reminder.
    if (payload.emailReminder && !session.reminderScheduledOnUtc) {
      session.reminderScheduledOnUtc = new Date().toISOString();
      pushOutbox("holdReminder", session.email, `Your submission is saved`);
    }
    writeMockSession(session);

    return {
      submissionId,
      status: "Saved",
      holdUntilUtc: session.holdUntilUtc,
      reminderScheduledOnUtc: session.reminderScheduledOnUtc || undefined,
    };
  },

  async recordPaywallExposure(
    payload: IPaywallExposureRequest
  ): Promise<IExperimentExposureResponse> {
    logCall("recordPaywallExposure", payload);
    await applySharedScenario(getMockScenario());

    const session = requireAuthenticatedSession();
    // Once per session per arm. A remount, a back-navigation or a second poll
    // must not inflate the denominator of the experiment.
    if (session.exposureRecorded && session.step4Variant === payload.variant) {
      return { recorded: false, arm: payload.variant, forced: !!payload.forced };
    }
    session.step4Variant = payload.variant;
    session.exposureRecorded = true;
    writeMockSession(session);
    return { recorded: true, arm: payload.variant, forced: !!payload.forced };
  },

  async recordCheckoutStarted(submissionId: number): Promise<void> {
    logCall("recordCheckoutStarted", { submissionId });
    await applySharedScenario(getMockScenario());
    const session = requireAuthenticatedSession();
    // Nothing is paid before the film center (tests C and D come through here too).
    if (mustChooseHome(session)) {
      throw apiError(409, "homeLocationRequired", "Mock: choose your country and film center first");
    }
    session.checkoutStartedFrom = submissionId;
    writeMockSession(session);
  },

  async recordPlanDeclined(submissionId: number): Promise<void> {
    logCall("recordPlanDeclined", { submissionId });
    await applySharedScenario(getMockScenario());
    requireAuthenticatedSession();
  },

  // The mock has no Stripe session to start: the model goes to its stand-in
  // checkout itself (submissionStep4Model.startDirectCheckout).
  async startDirectCheckout(submissionId: number): Promise<IDirectCheckoutResponse> {
    logCall("startDirectCheckout", { submissionId });
    await this.recordCheckoutStarted(submissionId);
    return { redirectUrl: null, fallback: "mock" };
  },
};

// --- helpers ---------------------------------------------------------------

// Demo outbox: window.__sfMockOutbox proves an email/SMS trigger fired exactly
// once without needing a mail server.
const pushOutbox = (kind: string, to: string, subject: string) => {
  try {
    const w = window as any;
    if (!w.__sfMockOutbox) w.__sfMockOutbox = [];
    w.__sfMockOutbox.push({ kind, to, subject, at: new Date().toISOString() });
  } catch (e) {
    // ignore
  }
};

const TAKEN_EMAILS = ["taken@theworkbook.com", "existing@theworkbook.com"];
const isTakenEmail = (email: string) =>
  TAKEN_EMAILS.indexOf((email || "").trim().toLowerCase()) > -1;

const EMAIL_MAX_LENGTH = 254;

// Mirrors ValidationHelper.IsEmailValid on the server: shape check PLUS the
// shared domain rejection list (emailDomainBlacklist.json — ~33k disposable
// providers — and emailDomainTypos.json — misspellings like "gmail.con").
// Without the domain check the mock accepted a@gmail.con, so the demo never
// exercised the 400 invalidEmail path the real API answers with, and a bad
// address could walk all the way to the code screen where nothing arrives.
const isPlausibleEmail = (email: string) => {
  const trimmed = (email || "").trim();
  return (
    !!trimmed && trimmed.length <= EMAIL_MAX_LENGTH && isValidEmail(trimmed)
  );
};

// Must match the client rule in components/Submission/common/validation.ts,
// NOT the legacy whitelist regex: otherwise the mock rejects passwords the
// form accepts ("Tr0mbone{Piano}", "contraseña1") and the demo shows an
// unactionable "must contain 6 characters, 1 letter and 1 number" error on a
// password that satisfies exactly that.
const isStrongEnough = (password: string) => isValidPassword(password);

const requireSession = (sessionId: string): IMockSession => {
  const session = readMockSession();
  if (!session || session.sessionId !== sessionId) {
    throw apiError(404, "sessionNotFound", "Mock: signup session not found");
  }
  return session;
};

const requireAuthenticatedSession = (): IMockSession => {
  const session = readMockSession();
  if (!session || !session.verified || !session.jwt) {
    throw apiError(401, "unauthorized", "Mock: not authenticated");
  }
  return session;
};

// --- step 4 helpers --------------------------------------------------------

// How many status polls the "webhook" takes to land after a successful payment.
const WEBHOOK_LAG_POLLS = 2;

// The well-known ids, resolved without the scenario branches. Shared with
// getBreakdownSubmissionInfo so the review screen and the entry gate can never
// describe the same breakdown differently.
// Through the API's late rule, as the real submission-info answers.
const breakdownFixture = (breakdownId: string, roleId?: string): IBreakdownSubmissionInfoDto =>
  withLateRule(breakdownFixtureAsStored(breakdownId, roleId));

const breakdownFixtureAsStored = (
  breakdownId: string,
  roleId?: string
): IBreakdownSubmissionInfoDto => {
  if (breakdownId === MOCK_IDS.closedBreakdown) {
    return { ...buildClosedBreakdown(roleId), breakdownId };
  }
  if (breakdownId === MOCK_IDS.roleCastBreakdown) {
    return { ...buildRoleCastBreakdown(Date.now(), roleId), breakdownId };
  }
  if (breakdownId === MOCK_IDS.allCastBreakdown) {
    return buildAllCastBreakdown();
  }
  if (breakdownId === MOCK_IDS.closingTodayBreakdown) {
    return buildClosingTodayBreakdown(roleId);
  }
  if (breakdownId === MOCK_IDS.crossBorderBreakdown) {
    return buildCrossBorderBreakdown(roleId);
  }
  if (breakdownId === MOCK_IDS.staleDeadlineBreakdown) {
    return buildStaleDeadlineBreakdown(roleId);
  }
  // Any other breakdown a list shows resolves to that list entry (the Mexico City and US
  // calls, Victoria…); only unknown ids fall back to Los Santos.
  const listed = breakdownId !== MOCK_IDS.openBreakdown ? buildListedBreakdown(breakdownId, roleId) : null;
  if (listed) return listed;
  return { ...buildOpenBreakdown(roleId), breakdownId };
};

// Closed by status, by the clock, or because the role itself is gone. The clock
// is included deliberately: a breakdown whose status is still "Open" a day
// after its deadline must not accept a submission nobody will read.
// The real API's midway rule (SubmissionFlowService.MidwayError): a role that
// isn't the breakdown's stops them, and so does a breakdown that can't be
// submitted to any more. One role cast while others are open goes on. Only the
// step-4 calls check it here: mock-seed walks a closed breakdown through the
// draft and the materials to reach step 4, and steps 2 and 3 already send it to
// the closed page on mount (refreshGate), as on the real API.
const isClosedBreakdown = (info: IBreakdownSubmissionInfoDto): boolean =>
  info.status === "Closed" ||
  (!info.isLate && Date.parse(info.closingDateUtc) <= Date.now());

const isForeignRole = (
  info: IBreakdownSubmissionInfoDto,
  roleId: string | null
): boolean => !!roleId && !info.selectedRole;

// The ACCOUNT's country — the corrected home country if the visitor fixed it,
// otherwise the geo guess it was created from. Never the ?ctry= override on its
// own, which is why this reads the session rather than the URL.
const accountCountry = (session: IMockSession): string =>
  (session.homeCountryCode || session.geoCountryCode || "CA").toUpperCase();

// The membership check: one function, so every screen and the send endpoint
// answer the same question the same way.
const membershipFor = (
  session: IMockSession,
  scenario: MockScenario
): ISubmissionMembershipDto => {
  const countryCode = accountCountry(session);
  // The US submits free. This is a property of the market, not a purchase, so
  // it is reported separately from membershipStatus — the paywall is skipped
  // and no trial is ever offered (docs/16).
  const freeMarket = countryCode === "US";

  let status = session.membershipStatus;
  let trialUsed = session.trialUsed;
  let eligible = true;
  if (scenario === "alreadyPro" || scenario === "existingPro") status = "pro";
  if (scenario === "trialActive") status = "trial";
  if (scenario === "usedTrial") {
    status = "free";
    trialUsed = true;
  }
  if (scenario === "notEligible") {
    status = "free";
    eligible = false;
  }

  const canSubmitFree = freeMarket || status === "pro" || status === "trial";
  const trialAvailable = !freeMarket && status === "free" && !trialUsed && eligible;

  let reason: ISubmissionMembershipDto["reason"] = "trialAvailable";
  if (freeMarket) reason = "freeMarket";
  else if (status === "pro") reason = "activePro";
  else if (status === "trial") reason = "activeTrial";
  else if (!eligible) reason = "notEligible";
  else if (trialUsed) reason = "trialUsed";

  return {
    membershipStatus: status,
    canSubmitFree,
    freeMarket,
    trialAvailable,
    trialDays: trialAvailable ? TRIAL_DAYS : undefined,
    reason,
    planEndsUtc:
      status === "trial"
        ? new Date(Date.now() + TRIAL_DAYS * 24 * 60 * 60 * 1000).toISOString()
        : undefined,
    countryCode,
  };
};

// Self-represented until the actor says otherwise — the account default, and
// what the review screen must show before anyone opens the sheet.
//
// The `||` is not belt-and-braces: a mock session stored by a build from before
// step 4 existed has no representationType at all, and JSON.parse hands it back
// exactly as saved. Without the fallback such a session rendered
// "Representation:" followed by nothing.
const representationFor = (session: IMockSession): IRepresentationDto => ({
  type: session.representationType || "selfRepresented",
  agencyId: session.representationAgencyId || undefined,
  agencyName: session.representationAgencyName || undefined,
  agentName: session.representationAgentName || undefined,
  invitedEmail: session.representationInvitedEmail || undefined,
});

const sentResponse = (session: IMockSession): ISendSubmissionResponse => {
  const info = breakdownFixture(session.breakdownId || "", session.roleId || undefined);
  const membership = membershipFor(session, getMockScenario());
  const city = resolveAccountCity({
    cityId: session.homeCityId || session.geoCityId,
    regionCode: session.geoRegionCode,
    countryCode: accountCountry(session),
  });
  return {
    status: "Sent",
    submissionId: session.submissionId || 0,
    sentOnUtc: session.sentOnUtc || new Date().toISOString(),
    breakdownName: info.breakdownName,
    roleName: info.selectedRole ? info.selectedRole.roleName : undefined,
    // Mock-only destination; the real one is the cross-domain dashboard handoff
    // that nobody owns yet (docs/13).
    dashboardUrl: "/submission/mock-dashboard",
    exploreCityId: city.city ? city.city.cityId : undefined,
    // The plan line belongs to a purchase the flow started, as on the server.
    plan: session.checkoutStartedFrom && membership.membershipStatus !== "free" ? membership.membershipStatus : null,
    planEndsUtc: session.checkoutStartedFrom ? membership.planEndsUtc : undefined,
    freeMarket: membership.freeMarket,
    freeCastingCall: !!info.allowFreeSubmissions,
  };
};

// The asset the store remembers, or a plausible stand-in. The fallback covers
// a session that predates the asset store and one whose bytes did not fit —
// the id is still real, so the flow's photo gate must keep passing.
const assetOr = (
  assetId: number | null,
  kind: MediaKind,
  fileName: string,
  sizeBytes: number
): IMediaAssetDto | undefined => {
  if (!assetId) return undefined;
  return (
    getMockAsset(assetId) ||
    MOCK_LIBRARY.filter((a) => a.assetId === assetId)[0] || {
      assetId,
      kind,
      fileName,
      sizeBytes,
      uploadedAtUtc: new Date().toISOString(),
    }
  );
};

// ?mockScenario=existingUser: what an existing member has in PM. Two photos (the first is the
// main headshot) and three resumes: two PDFs and one typed on theWorkbook, the newest the main.
const demoPhoto = (fill: string) =>
  `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="400" height="500"><rect width="400" height="500" fill="${fill}"/>` +
      `<circle cx="200" cy="190" r="80" fill="#ffffff" fill-opacity=".55"/>` +
      `<rect x="80" y="300" width="240" height="260" rx="120" fill="#ffffff" fill-opacity=".55"/>` +
      `<text x="200" y="460" font-family="sans-serif" font-size="22" font-weight="700" text-anchor="middle" fill="#1d2b44" fill-opacity=".5">DEMO PHOTO</text></svg>`
  )}`;

// The journeys capture (golden-path-journeys) shows its own headshot, as on the new account's
// walks: window.__mockLibraryPhotos, set before the app loads. Everyone else sees the silhouette.
const libraryPhoto = (index: number, fill: string): string => {
  const own = typeof window !== "undefined" && (window as any).__mockLibraryPhotos;
  return (own && own[index]) || demoPhoto(fill);
};

const MOCK_LIBRARY: IMediaAssetDto[] = [
  { assetId: 1, kind: "photo", fileName: "Headshot 1", url: libraryPhoto(0, "#b9c8d9"), sizeBytes: 1024 * 400, uploadedAtUtc: "2026-09-20T15:00:00Z", isMain: true },
  { assetId: 2, kind: "photo", fileName: "Headshot 2", url: libraryPhoto(1, "#d6c7b8"), sizeBytes: 1024 * 380, uploadedAtUtc: "2026-05-02T15:00:00Z" },
  { assetId: 9001, kind: "resume", fileName: "Paola Hernandez – Film & TV", sizeBytes: 0, uploadedAtUtc: "2026-10-12T15:00:00Z", resumeType: "pdf", pageCount: 2, isMain: true },
  { assetId: 9002, kind: "resume", fileName: "Paola Hernandez – Theatre", sizeBytes: 0, uploadedAtUtc: "2026-09-03T15:00:00Z", resumeType: "typed" },
  { assetId: 9003, kind: "resume", fileName: "Commercial resume", sizeBytes: 0, uploadedAtUtc: "2026-03-18T15:00:00Z", resumeType: "pdf", pageCount: 1 },
];

const toDraft = (session: IMockSession): ISubmissionDraftDto => ({
  submissionId: session.submissionId || 0,
  breakdownId: session.breakdownId,
  roleId: session.roleId,
  photo: assetOr(session.photoAssetId, "photo", "headshot.jpg", 1024 * 512),
  resume: assetOr(session.resumeAssetId, "resume", "resume.pdf", 1024 * 128),
  noteToCastingDirector: session.note,
  ageAttested: session.ageAttested,
  phoneVerified: session.phoneVerified,
  // Real values now that step 4 writes them — a saved draft used to keep
  // reporting "Draft" with no hold date, so the saved page had nothing to show.
  status: session.submissionStatus,
  holdUntilUtc: session.holdUntilUtc || undefined,
  updatedAtUtc: new Date().toISOString(),
  // Always there, as the real one: an account the lookup can't place is asked.
  home: mockHomeOrAsk(session),
});

// Verification couldn't place the visitor (the breakdown's city or nothing) and they haven't chosen since.
const unplaced = (session: IMockSession) =>
  !session.homeConfirmed && (session.geoSource === "breakdownFallback" || session.geoSource === "none");

// The real one's MustChooseHome: a new signup that's unplaced. Step 2 asks, and materials waits.
const mustChooseHome = (session: IMockSession) => mockNewSignup() && unplaced(session);

const mockHomeOrAsk = (session: IMockSession): ISubmissionHomeDto =>
  mockHome(session) || { cityId: 0, cityName: "", countryCode: "", located: false, newSignup: mockNewSignup() };

// The film center as the real API answers it: the account's city, and whether
// verification could place the visitor (the breakdown's city or nothing: ask).
const mockHome = (session: IMockSession): ISubmissionHomeDto | undefined => {
  const code = (session.homeCountryCode || "").toUpperCase();
  const city =
    CITY_OPTIONS.filter((c) => c.cityId === session.homeCityId)[0] ||
    CITY_OPTIONS.filter((c) => (c.countryCode || "").toUpperCase() === code)[0];
  if (!city) return undefined;
  return {
    cityId: city.cityId,
    cityName: city.cityName,
    countryCode: city.countryCode,
    located: !unplaced(session),
    newSignup: mockNewSignup(),
  };
};

// Every mock account is a new signup (this flow made it just now), except under
// ?mockScenario=existingUser (or existingPro, the same on a PRO plan): an older account, as the
// real 24-hour line draws it.
const mockNewSignup = () => {
  const scenario = getMockScenario();
  if (scenario === "existingUser" || scenario === "existingPro") return false;
  const session = readMockSession();
  return !(session && session.existingAccount);
};

// The classic site's logged-in actor, as the mock stands for it.
const MOCK_CLASSIC_ACTOR: IClassicSessionDto = { clientId: 9044821, firstName: "Paola", lastName: "Hernandez" };
const MOCK_CLASSIC_KEY = "sfMockClassicSession";
const readMockFlag = (key: string): boolean => {
  try {
    return window.sessionStorage.getItem(key) === "1";
  } catch (e) {
    return false;
  }
};
const writeMockFlag = (key: string) => {
  try {
    window.sessionStorage.setItem(key, "1");
  } catch (e) {
    // the tab forgets it; harmless in the mock
  }
};

export const resetMockBackend = () => {
  clearMockSession();
  // The uploaded bytes belong to the session that uploaded them; leaving them
  // behind would let a fresh session inherit the last one's headshot.
  clearMockAssets();
};
export { EMAIL_CODE_TTL_MIN };

export default mockSubmissionFlowApi;
